import { useNavigate } from 'react-router-dom';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button';
import { Card } from '../atoms/Card';
import { Inline } from '../atoms/Inline';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Stack } from '../atoms/Stack';
import { Text } from '../atoms/Typography';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { useTranslation } from '../../i18n/useTranslation';
import { useZeovitInputs } from '../../hooks/useZeovitRecommendations';
import type { ZeovitInputs } from '../../services/zeovitRecommendationService';
import { ZeovitVolumeRequiredNotice } from './ZeovitVolumeRequiredNotice';

export interface ZeovitInputsNoticeProps {
  aquariumId: string;
  /**
   * The user chose "continue anyway" in this flow: the update request is not shown
   * again. Owned by the page, so it survives the form being re-mounted after a retry.
   */
  acknowledged: boolean;
  onContinue: () => void;
}

/** 'YYYY-MM-DD' (a date) or an ISO timestamp → localised date. */
function formatDate(value: string, language: string): string {
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(language, { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
}

type UpdateReason = 'stale' | 'missing' | 'staleAndMissing';

function updateReason(inputs: ZeovitInputs): UpdateReason {
  const missing = inputs.missingParameters.length > 0;
  const stale = inputs.measurements.some((m) => m.stale);
  if (missing && stale) return 'staleAndMissing';
  return missing ? 'missing' : 'stale';
}

/**
 * Which stored data the ZeoVit recommendation / periodic check uses (v1.6.2: values
 * are no longer typed in). Minimal by design: one line naming the dates of the latest
 * measurements and ICP analysis; only when a core value is older than 14 days or
 * missing, one warning asks — once — for a new water test, with a "continue anyway"
 * path. Never blocks the submit.
 */
export function ZeovitInputsNotice({ aquariumId, acknowledged, onContinue }: ZeovitInputsNoticeProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: inputs, isLoading, isError, isFetching, refetch } = useZeovitInputs(aquariumId);
  const base = `/aquariums/${encodeURIComponent(aquariumId)}`;

  if (isLoading) {
    return (
      <LiveRegion busy>
        <Text color="muted" size="sm">
          {t('zeovit.inputs.loading')}
        </Text>
      </LiveRegion>
    );
  }

  if (isError || !inputs) {
    return (
      <LoadErrorState
        compact
        title={t('zeovit.inputs.loadError')}
        message={t('zeovit.inputs.loadErrorHint')}
        onRetry={() => {
          void refetch();
        }}
        retryLabel={t('zeovit.inputs.retry')}
        retrying={isFetching}
        data-testid="zeovit-inputs-error"
      />
    );
  }

  const language = i18n.language;
  const measurementDate = inputs.latestMeasurementAt ? formatDate(inputs.latestMeasurementAt, language) : '';
  const icpDate = inputs.icp ? formatDate(inputs.icp.reportDate, language) : '';
  let summary: string;
  if (measurementDate && icpDate) {
    summary = t('zeovit.inputs.summary.both', { measurementDate, icpDate });
  } else if (measurementDate) {
    summary = t('zeovit.inputs.summary.measurementsOnly', { measurementDate });
  } else if (icpDate) {
    summary = t('zeovit.inputs.summary.icpOnly', { icpDate });
  } else {
    summary = t('zeovit.inputs.summary.none');
  }

  const askForUpdate = inputs.needsUpdate && !acknowledged;
  // v3: the current dosing plans and the dosing log are part of every recommendation.
  const activePlans = inputs.dosing?.activePlanCount ?? null;
  const dosingSummary =
    activePlans === null
      ? null
      : activePlans > 0
        ? t('zeovit.inputs.dosing', { count: activePlans })
        : t('zeovit.inputs.dosingNone');

  return (
    <Stack gap="3" data-testid="zeovit-inputs">
      {inputs.volumeLiters === null ? (
        // Shown on page load, not after a user action: announced politely.
        <ZeovitVolumeRequiredNotice aquariumId={aquariumId} live="polite" />
      ) : null}
      {askForUpdate ? (
        <Alert
          tone="warning"
          // Shown on page load, not after a user action: announced politely.
          live="polite"
          data-testid="zeovit-inputs-update"
          action={
            <Inline gap="2" wrap>
              <Button variant="secondary" onClick={() => navigate(`${base}/measurements/new`)}>
                {t('zeovit.inputs.addWaterTest')}
              </Button>
              <Button variant="ghost" onClick={onContinue}>
                {t('zeovit.inputs.continueAnyway')}
              </Button>
            </Inline>
          }
        >
          {t(`zeovit.inputs.update.${updateReason(inputs)}`, { days: inputs.staleAfterDays })}
        </Alert>
      ) : null}
      <Card padding="sm">
        <Stack gap="2">
          <Text size="sm" color="muted" data-testid="zeovit-inputs-summary">
            {summary}
          </Text>
          {dosingSummary ? (
            <Text size="sm" color="muted" data-testid="zeovit-inputs-dosing">
              {dosingSummary}
            </Text>
          ) : null}
          {inputs.icp ? null : (
            <Inline>
              <Button variant="ghost" size="sm" onClick={() => navigate(`${base}/icp/upload`)}>
                {t('zeovit.inputs.uploadIcp')}
              </Button>
            </Inline>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}

export default ZeovitInputsNotice;
